import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { TEST_SIGNAL_PATHS, type UseLatestCameraTestSignalsResult } from "@visioni/a3s-monitoring/app";
import { SIGNAL_PATH_LABELS } from "../log-rows";
import { signalPathColors } from "../signal-path-colors";
import { toCommandSummaryRow, toSummaryRow } from "../summary-rows";
import { formatOccurredAt } from "./format-time";
import { SIGNAL_PATH_STRIPE_WIDTH, toneColor } from "./TestSignalLog";

/** Keeps the two legs side by side on a desktop, stacking on a phone. */
const COLUMN_MIN_WIDTH = 260;

/** "7 Sept" — the window start, where the time of day would only be noise. */
function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

/**
 * The newest signal per leg and trigger type, above the log.
 *
 * Answers "is this camera calling home?" without reading the log: the log
 * interleaves both legs and both triggers, so the answer is otherwise spread
 * across rows, and possibly across pages.
 */
export function TestSignalSummary({ summary }: { summary: UseLatestCameraTestSignalsResult }) {
  const { theme } = useUnistyles();

  const { since } = summary;

  return (
    <View gap="xl">
      <Text textStyle="sm/semibold" color="text">
        Latest signals
      </Text>
      {summary.isError ?
        <Text textStyle="sm/regular" color="danger">
          Could not load the latest signals.
        </Text>
      : <View flexDirection="row" flexWrap="wrap" gap="3xl">
          {TEST_SIGNAL_PATHS.map((path) => (
            <View
              key={path}
              // Two layers, as in the log: the card keeps its opaque surface and
              // the tint washes over it, rather than replacing it with a
              // translucent one that lets the page show through.
              style={{ flexGrow: 1, flexBasis: COLUMN_MIN_WIDTH, overflow: "hidden" }}
              bg="surfaceCardInner"
              borderRadius={theme.spacing.lg}
              borderWidth={1}
              borderColor="borderCard"
            >
              <View
                style={{
                  flexGrow: 1,
                  borderLeftWidth: SIGNAL_PATH_STRIPE_WIDTH,
                  borderLeftColor: signalPathColors(path, theme).fill,
                  backgroundColor: signalPathColors(path, theme).tint,
                }}
                p="3xl"
                gap="md"
              >
                <Text textStyle="sm/semibold" color="text">
                  {SIGNAL_PATH_LABELS[path]}
                </Text>
                {[
                  ...summary.latest.signals.filter((cell) => cell.signalPath === path).map(toSummaryRow),
                  ...summary.latest.commands.filter((cell) => cell.signalPath === path).map(toCommandSummaryRow),
                ].map((row) => (
                  <View key={row.key} gap="xs">
                    <View flexDirection="row" alignItems="baseline" gap="xs">
                      <Text textStyle="xs/regular" color="textMuted">
                        {row.label}:
                      </Text>
                      <Text
                        textStyle="sm/regular"
                        // An absence carries its own tone: a missing heartbeat is the
                        // thing on this card most worth noticing.
                        style={{
                          color:
                            since !== null && row.occurredAt === null ? toneColor(row.tone, theme) : theme.colors.text,
                        }}
                      >
                        {/* A signal already in the loaded log is real whether or not
                            the server has answered; an absence is only known once
                            it has. */}
                        {row.occurredAt !== null ?
                          formatOccurredAt(row.occurredAt)
                        : since === null ?
                          "Loading…"
                        : `None since ${formatDay(since)}`}
                      </Text>
                    </View>
                    {row.verdict !== null && (
                      <Text textStyle="xs/regular" style={{ color: toneColor(row.tone, theme) }}>
                        {row.verdict}
                      </Text>
                    )}
                  </View>
                ))}
              </View>
            </View>
          ))}
        </View>
      }
    </View>
  );
}
